import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { useWindowSize } from '../utils/useWindowSize';
import { getPractitionerActivities, createPractitionerActivity, updatePractitionerActivity, deletePractitionerActivity, getSessions, getSessionParticipants, getPractitioners } from '../utils/api';

const PractitionerActivities = () => {
  const { isMobile } = useWindowSize();
  const [activities, setActivities] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', frequency: 'daily', daysOfWeek: [], timesPerDay: 1, slotLabels: ['Daily'], startDate: new Date().toISOString().split('T')[0], endDate: '', trackCompliance: true });
  const [assignMode, setAssignMode] = useState('manual');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [sessionUsers, setSessionUsers] = useState([]);
  const [loadingSession, setLoadingSession] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(function() {
    Promise.all([getPractitionerActivities(), getPractitioners(), getSessions()])
      .then(function(results) {
        setActivities(results[0].data || []);
        setPractitioners(results[1].data.practitioners || []);
        setSessions(results[2].data.sessions || []);
      })
      .catch(function() {})
      .finally(function() { setLoading(false); });
  }, []);

  function computeCompliance(act, assignee) {
    if (!act.trackCompliance) return null;
    var start = new Date(act.startDate);
    var end = act.endDate ? new Date(act.endDate) : new Date();
    var days = [];
    var cur = new Date(start);
    while (cur <= end) {
      var ds = cur.toISOString().split('T')[0];
      var dow = cur.getDay();
      var ok = act.frequency === 'daily' ? true : ((act.daysOfWeek || []).length === 0 || act.daysOfWeek.includes(dow));
      if (ok) days.push(ds);
      cur.setDate(cur.getDate() + 1);
    }
    var total = days.length * (act.timesPerDay || 1);
    if (total === 0) return 100;
    var done = (assignee.checkIns || []).filter(function(c) { return days.includes(c.date); }).length;
    return Math.min(100, Math.round((done / total) * 100));
  }

  function openNew() {
    setEditId(null);
    setForm({ title: '', description: '', frequency: 'daily', daysOfWeek: [], timesPerDay: 1, slotLabels: ['Daily'], startDate: new Date().toISOString().split('T')[0], endDate: '', trackCompliance: true });
    setSelectedUsers([]);
    setSessionId('');
    setSessionUsers([]);
    setAssignMode('manual');
    setModalOpen(true);
  }

  function openEdit(act) {
    setEditId(act._id);
    setForm({ title: act.title, description: act.description || '', frequency: act.frequency, daysOfWeek: act.daysOfWeek || [], timesPerDay: act.timesPerDay || 1, slotLabels: act.slotLabels || ['Daily'], startDate: act.startDate ? act.startDate.split('T')[0] : '', endDate: act.endDate ? act.endDate.split('T')[0] : '', trackCompliance: act.trackCompliance });
    setSelectedUsers((act.assignees || []).map(function(a) { return { user: a.user, name: a.name, role: a.role }; }));
    setAssignMode('manual');
    setSessionId('');
    setSessionUsers([]);
    setModalOpen(true);
  }

  function handleSave() {
    if (!form.title.trim()) { alert('Please enter a title.'); return; }
    var assigneeList = assignMode === 'session' ? sessionUsers : selectedUsers;
    if (assigneeList.length === 0) { alert('Please select at least one person.'); return; }
    setSaving(true);
    var payload = Object.assign({}, form, { endDate: form.endDate || null, assignees: assigneeList });
    var promise = editId ? updatePractitionerActivity(editId, payload) : createPractitionerActivity(payload);
    promise.then(function(res) {
      if (editId) {
        setActivities(function(prev) { return prev.map(function(a) { return a._id === editId ? res.data : a; }); });
      } else {
        setActivities(function(prev) { return [res.data].concat(prev); });
      }
      setModalOpen(false);
      setEditId(null);
    }).catch(function() { alert('Save failed.'); }).finally(function() { setSaving(false); });
  }

  function handleDelete(id) {
    if (!window.confirm('Delete this activity?')) return;
    deletePractitionerActivity(id).then(function() {
      setActivities(function(prev) { return prev.filter(function(a) { return a._id !== id; }); });
    }).catch(function() { alert('Delete failed.'); });
  }

  function handleSessionPick(sid) {
    setSessionId(sid);
    if (!sid) { setSessionUsers([]); return; }
    setLoadingSession(true);
    getSessionParticipants(sid).then(function(res) {
      var participants = (res.data.participants || []).map(function(p) {
        var u = p.student || p;
        return { user: u._id || u, name: ((u.firstName || '') + ' ' + (u.lastName || '')).trim(), role: u.role || 'student' };
      });
      setSessionUsers(participants);
    }).catch(function() { setSessionUsers([]); }).finally(function() { setLoadingSession(false); });
  }

  function toggleUser(u) {
    var id = u._id;
    setSelectedUsers(function(prev) {
      var exists = prev.find(function(x) { return x.user === id; });
      if (exists) return prev.filter(function(x) { return x.user !== id; });
      return prev.concat([{ user: id, name: (u.firstName + ' ' + u.lastName).trim(), role: u.role || '' }]);
    });
  }

  var s = styles(isMobile);

  if (loading) return (
    <div style={s.layout}>
      <Sidebar />
      <div style={s.main}><div style={{ padding: '40px', color: '#666' }}>Loading...</div></div>
    </div>
  );

  return (
    <div style={s.layout}>
      <Sidebar />
      <div style={s.main}>
        {/* Header */}
        <div style={s.header}>
          <div>
            <h1 style={s.headerTitle}>Practitioner Activities</h1>
            <div style={s.headerSub}>Assign daily practices to healers and students</div>
          </div>
          <button onClick={openNew} style={s.addBtn}>+ New Activity</button>
        </div>

        {/* Activity list */}
        {activities.length === 0 ? (
          <div style={s.emptyCard}>
            <div style={s.emptyTitle}>No activities yet</div>
            <div style={s.emptySub}>Click "New Activity" to assign a practice to your healers or students.</div>
          </div>
        ) : activities.map(function(act) {
          var isExpanded = expandedId === act._id;
          var overallCompliance = act.trackCompliance && (act.assignees || []).length > 0
            ? Math.round((act.assignees || []).reduce(function(sum, a) { var c = computeCompliance(act, a); return sum + (c !== null ? c : 0); }, 0) / act.assignees.length)
            : null;
          var freqLabel = act.frequency === 'daily' ? 'Every day' : act.frequency === 'weekly' ? 'Weekly' : 'Custom days';
          return (
            <div key={act._id} style={s.card}>
              {/* Card header */}
              <div style={s.cardTop}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={s.actTitle}>{act.title}</span>
                    <span style={{ ...s.pill, background: act.isActive ? '#eafaf1' : '#f5f5f5', color: act.isActive ? '#27ae60' : '#999' }}>{act.isActive ? 'Active' : 'Inactive'}</span>
                    {act.trackCompliance && overallCompliance !== null && (
                      <span style={{ ...s.pill, background: overallCompliance >= 70 ? '#eafaf1' : overallCompliance >= 40 ? '#fef9e7' : '#fdf0f0', color: overallCompliance >= 70 ? '#27ae60' : overallCompliance >= 40 ? '#d4ac0d' : '#e74c3c' }}>{overallCompliance}% compliance</span>
                    )}
                    {!act.trackCompliance && <span style={{ ...s.pill, background: '#f0f0f0', color: '#999' }}>No tracking</span>}
                  </div>
                  <div style={s.actMeta}>
                    {(act.assignees || []).length} assignee{(act.assignees || []).length !== 1 ? 's' : ''} · {freqLabel} · {act.timesPerDay || 1}× {act.frequency === 'daily' ? 'per day' : 'per session'} · Starts {new Date(act.startDate).toLocaleDateString('en-IN')}{act.endDate ? ' → ' + new Date(act.endDate).toLocaleDateString('en-IN') : ''}
                    {act.description ? ' · ' + act.description : ''}
                  </div>
                </div>
                <div style={s.cardActions}>
                  <button onClick={function() { setExpandedId(isExpanded ? null : act._id); }} style={s.btnOutline}>{isExpanded ? 'Collapse' : 'View Details'}</button>
                  <button onClick={function() { openEdit(act); }} style={{ ...s.btnOutline, borderColor: '#4a8f82', color: '#4a8f82' }}>Edit</button>
                  <button onClick={function() { handleDelete(act._id); }} style={{ ...s.btnOutline, borderColor: '#e74c3c', color: '#e74c3c' }}>Delete</button>
                </div>
              </div>

              {/* Expanded: per-assignee table */}
              {isExpanded && (
                <div style={{ padding: '0 20px 16px' }}>
                  {!(act.assignees || []).length ? (
                    <div style={{ color: '#999', fontSize: '13px' }}>No assignees.</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#f5f8f7' }}>
                          <th style={s.th}>Name</th>
                          <th style={s.th}>Role</th>
                          {act.trackCompliance && <th style={{ ...s.th, textAlign: 'center' }}>Check-ins</th>}
                          {act.trackCompliance && <th style={{ ...s.th, textAlign: 'center' }}>Compliance</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(act.assignees || []).map(function(a, ai) {
                          var comp = computeCompliance(act, a);
                          return (
                            <tr key={ai} style={{ borderBottom: '1px solid #f0f0f0', background: ai % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <td style={{ padding: '8px 12px', color: '#333', fontWeight: '600' }}>{a.name}</td>
                              <td style={{ padding: '8px 12px' }}>
                                <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', background: a.role === 'student' ? '#eaf4fb' : '#f0f0f0', color: a.role === 'student' ? '#2980b9' : '#666', textTransform: 'uppercase' }}>{a.role}</span>
                              </td>
                              {act.trackCompliance && <td style={{ padding: '8px 12px', textAlign: 'center', color: '#555' }}>{(a.checkIns || []).length}</td>}
                              {act.trackCompliance && (
                                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                  {comp !== null ? <span style={{ fontWeight: '700', color: comp >= 70 ? '#27ae60' : comp >= 40 ? '#d4ac0d' : '#e74c3c' }}>{comp}%</span> : '—'}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Create / Edit Modal */}
        {modalOpen && (
          <div style={s.overlay}>
            <div style={s.modal}>
              <div style={s.modalTitle}>{editId ? 'Edit Activity' : 'New Practitioner Activity'}</div>

              <div style={s.field}>
                <label style={s.label}>Title *</label>
                <input value={form.title} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, { title: e.target.value }); }); }}
                  style={s.input} placeholder="e.g. Twin Hearts Meditation" />
              </div>

              <div style={s.field}>
                <label style={s.label}>Instructions / Description</label>
                <textarea value={form.description} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, { description: e.target.value }); }); }}
                  rows={2} style={{ ...s.input, resize: 'vertical' }} />
              </div>

              <div style={s.twoCol}>
                <div style={s.field}>
                  <label style={s.label}>Start Date *</label>
                  <input type="date" value={form.startDate} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, { startDate: e.target.value }); }); }} style={s.input} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>End Date (blank = indefinite)</label>
                  <input type="date" value={form.endDate} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, { endDate: e.target.value }); }); }} style={s.input} />
                </div>
              </div>

              <div style={s.twoCol}>
                <div style={s.field}>
                  <label style={s.label}>Frequency</label>
                  <select value={form.frequency} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, { frequency: e.target.value, daysOfWeek: [] }); }); }} style={s.select}>
                    <option value="daily">Daily (every day)</option>
                    <option value="weekly">Weekly (specific day/s)</option>
                    <option value="custom">Custom days</option>
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.label}>{form.frequency === 'daily' ? 'Times per day' : 'Times per session'}</label>
                  <select value={form.timesPerDay} onChange={function(e) {
                    var n = parseInt(e.target.value);
                    var labels = n === 1 ? ['Daily'] : n === 2 ? ['Morning', 'Evening'] : ['Morning', 'Afternoon', 'Evening'];
                    setForm(function(p) { return Object.assign({}, p, { timesPerDay: n, slotLabels: labels }); });
                  }} style={s.select}>
                    <option value={1}>{form.frequency === 'daily' ? '1× per day' : '1× per session'}</option>
                    <option value={2}>{form.frequency === 'daily' ? '2× per day' : '2× per session'}</option>
                    <option value={3}>{form.frequency === 'daily' ? '3× per day' : '3× per session'}</option>
                  </select>
                </div>
              </div>

              {(form.frequency === 'weekly' || form.frequency === 'custom') && (
                <div style={s.field}>
                  <label style={s.label}>Days of Week</label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(function(d, i) {
                      var on = (form.daysOfWeek || []).includes(i);
                      return (
                        <button key={d} type="button" onClick={function() {
                          setForm(function(p) {
                            var days = on ? (p.daysOfWeek || []).filter(function(x) { return x !== i; }) : (p.daysOfWeek || []).concat([i]);
                            return Object.assign({}, p, { daysOfWeek: days });
                          });
                        }} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid ' + (on ? '#1a6b5a' : '#ddd'), background: on ? '#1a6b5a' : '#fff', color: on ? '#fff' : '#555', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>{d}</button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <input type="checkbox" id="trackComp" checked={form.trackCompliance} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, { trackCompliance: e.target.checked }); }); }} />
                <label htmlFor="trackComp" style={{ fontSize: '13px', color: '#333', cursor: 'pointer' }}>Track compliance (assignees mark done daily)</label>
              </div>

              <div style={s.field}>
                <label style={s.label}>Assign To</label>
                <div style={{ display: 'flex', gap: '4px', background: '#f5f5f5', borderRadius: '6px', padding: '4px', marginBottom: '12px' }}>
                  {[{ key: 'manual', label: 'Select People' }, { key: 'session', label: 'By Session' }].map(function(m) {
                    return (
                      <button key={m.key} type="button" onClick={function() { setAssignMode(m.key); }}
                        style={{ flex: 1, padding: '8px', border: 'none', borderRadius: '4px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', background: assignMode === m.key ? '#1a6b5a' : 'transparent', color: assignMode === m.key ? '#fff' : '#666' }}>
                        {m.label}
                      </button>
                    );
                  })}
                </div>

                {assignMode === 'manual' && (
                  <div style={{ border: '1px solid #e0e0e0', borderRadius: '6px', maxHeight: '220px', overflowY: 'auto' }}>
                    {practitioners.length === 0 ? (
                      <div style={{ padding: '12px', color: '#999', fontSize: '13px' }}>No healers/students found.</div>
                    ) : practitioners.map(function(u) {
                      var selected = selectedUsers.some(function(x) { return x.user === u._id; });
                      return (
                        <div key={u._id} onClick={function() { toggleUser(u); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer', background: selected ? '#f0f7f5' : '#fff', borderBottom: '1px solid #f5f5f5' }}>
                          <input type="checkbox" readOnly checked={selected} style={{ flexShrink: 0 }} />
                          <span style={{ fontSize: '13px', color: '#333', fontWeight: selected ? '700' : '400' }}>{u.firstName} {u.lastName}</span>
                          <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '20px', background: u.role === 'student' ? '#eaf4fb' : '#f0f0f0', color: u.role === 'student' ? '#2980b9' : '#666', marginLeft: 'auto', textTransform: 'uppercase' }}>{u.role}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {assignMode === 'session' && (
                  <div>
                    <select value={sessionId} onChange={function(e) { handleSessionPick(e.target.value); }} style={{ ...s.select, marginBottom: '8px' }}>
                      <option value="">Select a session...</option>
                      {sessions.map(function(sess) {
                        return <option key={sess._id} value={sess._id}>{sess.title} — {new Date(sess.date).toLocaleDateString('en-IN')}</option>;
                      })}
                    </select>
                    {loadingSession && <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>Loading enrollees...</div>}
                    {sessionUsers.length > 0 && (
                      <div style={{ fontSize: '12px', color: '#555', padding: '8px 12px', background: '#f5f8f7', borderRadius: '6px' }}>
                        {sessionUsers.length} enrolled: {sessionUsers.map(function(u) { return u.name; }).join(', ')}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ marginTop: '8px', fontSize: '11px', color: '#888' }}>
                  {assignMode === 'manual' ? selectedUsers.length + ' selected' : sessionUsers.length + ' from session'}
                </div>
              </div>

              <div style={s.modalFooter}>
                <button type="button" onClick={function() { setModalOpen(false); setEditId(null); }} style={s.btnCancel}>Cancel</button>
                <button type="button" onClick={handleSave} disabled={saving} style={{ ...s.addBtn, opacity: saving ? 0.7 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Saving...' : (editId ? 'Save Changes' : 'Create & Assign')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = function(isMobile) {
  return {
    layout: { display: 'flex', minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" },
    main: { marginLeft: isMobile ? 0 : '260px', flex: 1, padding: isMobile ? '16px' : '32px' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' },
    headerTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '26px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 4px' },
    headerSub: { fontSize: '13px', color: '#666', margin: 0 },
    addBtn: { padding: '10px 22px', background: '#1a6b5a', color: '#fff', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.5px' },
    card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '12px', overflow: 'hidden' },
    cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', gap: '12px', flexWrap: 'wrap' },
    cardActions: { display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 },
    actTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e' },
    actMeta: { fontSize: '12px', color: '#666', marginTop: '5px' },
    pill: { fontSize: '10px', fontWeight: '700', padding: '2px 9px', borderRadius: '20px' },
    btnOutline: { padding: '6px 12px', background: 'transparent', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '11px', fontWeight: '700', color: '#555', cursor: 'pointer' },
    th: { padding: '8px 12px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.8px', borderBottom: '1px solid #e0e0e0', background: '#f5f8f7' },
    emptyCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '48px', textAlign: 'center' },
    emptyTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' },
    emptySub: { fontSize: '13px', color: '#999' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
    modal: { background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '680px', maxHeight: '90vh', overflowY: 'auto', padding: '28px' },
    modalTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '17px', fontWeight: '700', color: '#1a1a2e', marginBottom: '22px' },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' },
    field: { marginBottom: '16px' },
    label: { display: 'block', fontSize: '11px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', fontFamily: "'Open Sans', sans-serif" },
    select: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
    btnCancel: { padding: '10px 20px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', fontSize: '13px', cursor: 'pointer' },
  };
};

export default PractitionerActivities;
